import * as React from "react";

import { useAuth } from "@/providers/auth";

import { CommentRecord } from "../CommentRecord";

/** User IDs for system users */
const ROBOTO_PLATFORM_USER_IDS = ["support@roboto.ai", "system@roboto.ai"];

/**
 * Comment record with additional user information for display purposes.
 */
export interface UIComment extends CommentRecord {
  /** URL of the comment author's profile picture */
  avatarUrl: string | undefined;

  /** Display name of the comment author */
  creatorDisplayName?: string;

  /** Whether the comment author is a system user */
  isSystemUser: boolean;
}

/**
 * Enriches comment records with user information (avatar URL and display name)
 * from the organization roles.
 *
 * @param comments - Array of comment records to enrich
 * @returns Array of enriched comments with avatar and display name information
 */
export function useUIComments(comments: CommentRecord[]): UIComment[] {
  const { orgRoles } = useAuth();

  return React.useMemo(() => {
    if (!orgRoles) {
      return comments.map(
        (comment): UIComment => ({
          ...comment,
          avatarUrl: undefined,
          isSystemUser: ROBOTO_PLATFORM_USER_IDS.includes(comment.created_by),
        }),
      );
    }

    // Build lookup maps from org roles
    const avatarMap = new Map<string, string | null | undefined>();
    const userNameMap = new Map<string, string>();

    orgRoles.forEach((role) => {
      avatarMap.set(role.user.user_id, role.user.picture_url);
      if (role.user.name) {
        userNameMap.set(role.user.user_id, role.user.name);
      }
    });

    // Enrich comments with avatar and username data
    return comments.map(
      (comment): UIComment => ({
        ...comment,
        avatarUrl: avatarMap.get(comment.created_by) ?? undefined,
        creatorDisplayName: userNameMap.get(comment.created_by),
        isSystemUser: ROBOTO_PLATFORM_USER_IDS.includes(comment.created_by),
      }),
    );
  }, [comments, orgRoles]);
}
